import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { getWageSuggestion, WageSuggestionResponse } from "../../api/bluecrew/wageSuggestions";

interface WageSuggestionContentProps {
  visible: boolean;
  zipcode?: string;
  jobType?: string;
  city?: string;
  state?: string;
}

export const WageSuggestionContent = ({
  visible,
  zipcode,
  jobType,
  city,
  state,
}: WageSuggestionContentProps) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<WageSuggestionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible && zipcode && jobType) {
      fetchWageSuggestion();
    }
  }, [visible, zipcode, jobType, city, state]);

  const fetchWageSuggestion = async () => {
    if (!zipcode || !jobType) return;

    setLoading(true);
    setError(null);
    try {
      const result = await getWageSuggestion({ zipcode, jobType, city, state });
      setData(result);
    } catch (err) {
      setError("Failed to fetch wage suggestion");
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <Container>
      {loading && <Text>Loading wage suggestion...</Text>}

      {error && <ErrorText>{error}</ErrorText>}

      {!loading && !error && data && (
        <>
          {data.status === "SUCCESS" && data.suggestedWage && (
            <SuccessContainer>
              <WageAmount>${data.suggestedWage}/hr</WageAmount>
              <Details>
                Based on {data.sampleSize} jobs in {data.location || zipcode}
                {data.fallbackLevel && data.fallbackLevel !== "zipcode" && (
                  <> ({data.fallbackLevel}-level data)</>
                )}
              </Details>
            </SuccessContainer>
          )}

          {data.status === "ND" && (
            <Text>Insufficient data available for this location and job type.</Text>
          )}

          {data.status === "ERROR" && (
            <ErrorText>{data.message || "An error occurred while fetching wage suggestion."}</ErrorText>
          )}
        </>
      )}

      {!loading && !error && !data && zipcode && jobType && (
        <Text>Click {'"'}Suggested Wage{'"'} to view wage data for this position.</Text>
      )}

      {!loading && !error && (!zipcode || !jobType) && (
        <Text>Please select a worksite location and ensure job type is set to view wage suggestions.</Text>
      )}
    </Container>
  );
};

const Container = styled.div`
  margin-top: 16px;
  padding: 16px;
  background-color: #f7f7f7;
  border-radius: 4px;
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const WageAmount = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #000000;
`;

const Details = styled.div`
  font-size: 12px;
  color: #666666;
`;

const Text = styled.div`
  font-size: 12px;
  color: #666666;
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: #e11900;
`;
